/* tslint:disable:member-ordering */
import {
  Component,
  ElementRef,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Self,
} from '@angular/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ControlValueAccessor, FormBuilder, FormGroup, NgControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { FocusMonitor } from '@angular/cdk/a11y';
import { LoggerService } from '../../../../sdk';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import fromPairs from 'lodash-es/fromPairs';
import isNil from 'lodash-es/isNil';

@Component({
  selector: 'app-time-grid',
  templateUrl: './time-grid.component.html',
  styleUrls: ['./time-grid.component.scss'],
  providers: [{ provide: MatFormFieldControl, useExisting: forwardRef(() => TimeGridComponent) }],
})
export class TimeGridComponent implements MatFormFieldControl<any[]>, ControlValueAccessor, OnInit, OnDestroy {
  static nextId = 0;

  parts: FormGroup;

  stateChanges = new Subject<void>();

  focused = false;

  errorState = false;

  controlType = 'app-time-grid';

  @HostBinding('attr.id') id = `app-time-grid-${TimeGridComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';
  @HostBinding('class.floating') get shouldLabelFloat() {
    return true;
  }

  /** `View -> model callback called when value changes` */
  _onChange: (value: any) => void = () => {};

  /** `View -> model callback called when select has been touched` */
  _onTouched = () => {};

  constructor(
    private fb: FormBuilder,
    private fm: FocusMonitor,
    private elRef: ElementRef,
    @Optional() @Self() public ngControl: NgControl,
    public logger: LoggerService,
  ) {
    // Setting the value accessor directly (instead of using
    // the providers) to avoid running into a circular import.
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    fm.monitor(elRef.nativeElement, true).subscribe(origin => {
      this.focused = !!origin;
      this.stateChanges.next();
    });
  }

  ngOnInit(): void {
    this.parts = this.fb.group(fromPairs(this.labels.map(l => [l, null])));

    this.parts.valueChanges.subscribe(parts => {
      this._onChange(this.labels.map(l => parts[l]));
    });
  }

  @Input() labels: string[] = [];

  private _placeholder: string;
  get placeholder() {
    return this._placeholder;
  }

  @Input()
  set placeholder(plh) {
    setTimeout(() => {
      this._placeholder = plh;
      this.stateChanges.next();
    });
  }

  private _required = false;
  get required() {
    return this._required;
  }

  @Input()
  set required(req) {
    setTimeout(() => {
      this._required = coerceBooleanProperty(req);
      this.stateChanges.next();
    });
  }

  private _disabled = false;
  get disabled() {
    return this._disabled;
  }

  @Input()
  set disabled(dis) {
    setTimeout(() => {
      this._disabled = coerceBooleanProperty(dis);
      this.stateChanges.next();
    });
  }

  private _readonly = false;
  get readonly() {
    return this._readonly;
  }

  @Input()
  set readonly(val) {
    setTimeout(() => {
      this._readonly = coerceBooleanProperty(val);
      this.stateChanges.next();
    });
  }

  get value(): any[] | null {
    const n = this.parts.value;
    return this.labels.map(l => n[l]);
  }

  @Input()
  set value(val: any[] | null) {
    val = val || this.labels.map(l => null);

    setTimeout(() => {
      this.parts.setValue(fromPairs(this.labels.map((l, idx) => [l, val[idx]])));
      this.stateChanges.next();
    });
  }

  get empty() {
    const n = this.parts.value;
    return this.labels.map(l => n[l]).every(v => isNil(v));
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this.fm.stopMonitoring(this.elRef.nativeElement);
  }

  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent) {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.elRef.nativeElement.querySelector('input').focus();
    }
  }

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this._onTouched = fn;
  }
}
